'use client'
import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Loads the Zoho Desk ASAP widget only after the user interacts with the page
 * (scroll, click, touch, or keyboard). This defers all third-party cookies
 * and network requests until they're actually needed.
 */
const ZohoDeskWidget = () => {
  const [shouldLoad, setShouldLoad] = useState(false)
  const loaded = useRef(false)

  const triggerLoad = useCallback(() => {
    if (loaded.current) return
    loaded.current = true
    setShouldLoad(true)
  }, [])

  // Listen for first user interaction, then load
  useEffect(() => {
    const events = ['scroll', 'click', 'touchstart', 'keydown'] as const
    events.forEach((e) => window.addEventListener(e, triggerLoad, { once: true, passive: true }))
    return () => {
      events.forEach((e) => window.removeEventListener(e, triggerLoad))
    }
  }, [triggerLoad])

  // Guard against Zoho overriding the viewport meta tag
  useEffect(() => {
    if (!shouldLoad) return
    const viewport = document.querySelector('meta[name="viewport"]')
    if (!viewport) return
    const observer = new MutationObserver(() => {
      if (viewport.getAttribute('content')?.includes('user-scalable=no')) {
        viewport.setAttribute('content', 'width=device-width, initial-scale=1')
      }
    })
    observer.observe(viewport, { attributes: true })
    return () => observer.disconnect()
  }, [shouldLoad])

  // Inject the Zoho script only after first interaction
  useEffect(() => {
    if (!shouldLoad) return
    if (document.getElementById('zohodeskasapscript')) return

    const script = document.createElement('script')
    script.id = 'zohodeskasapscript'
    script.type = 'text/javascript'
    script.defer = true
    script.src =
      'https://desk.zoho.com/portal/api/web/asapApp/1126959000000482039?orgId=886637641'
    document.head.appendChild(script)

    ;(window as any).ZohoDeskAsapReady = function (cb: (() => void) | undefined) {
      const queue: (() => void)[] =
        ((window as any).ZohoDeskAsap__asyncalls as (() => void)[] | undefined) ?? []
      ;(window as any).ZohoDeskAsap__asyncalls = queue
      if ((window as any).ZohoDeskAsapReadyStatus) {
        if (cb) queue.push(cb)
        queue.forEach((fn) => fn())
        ;(window as any).ZohoDeskAsap__asyncalls = null
      } else if (cb) {
        queue.push(cb)
      }
    }
  }, [shouldLoad])

  return null
}

export default ZohoDeskWidget
